import { getA1Notation } from '@/app/gridGL/UI/gridHeadings/getA1Notation';
import { sheets } from '@/app/grid/controller/Sheets';
import type { A1Selection, Validation, ValidationUpdate } from '@/app/quadratic-core-types';

export const ValidationRuleSimpleValues = ['text', 'number', 'list', 'list-range', 'logical', 'date', 'custom', 'none'];
export type ValidationRuleSimple =
  | 'text'
  | 'number'
  | 'list'
  | 'list-range'
  | 'logical'
  | 'date'
  | 'custom'
  | 'none'
  | '';
export type ValidationUndefined = Validation | Omit<ValidationUpdate, 'rule'> | ValidationUpdate | undefined;
export type ValidationUIType = 'checkbox' | 'dropdown';

// Converts a Validation to a ValidationRuleSimple (to make it easier to work with)
export const validationRuleSimple = (validation?: ValidationUndefined): ValidationRuleSimple => {
  if (!validation || !('rule' in validation) || !validation.rule) return '';
  const rule = validation.rule;
  if (rule === 'None') return 'none';
  if ('List' in rule) {
    if ('source' in rule.List) {
      if ('List' in rule.List.source) return 'list';
      if ('Selection' in rule.List.source) return 'list-range';
    }
    return 'list';
  } else if ('Logical' in rule) {
    return 'logical';
  } else if ('Text' in rule) {
    return 'text';
  } else if ('Number' in rule) {
    return 'number';
  } else if ('DateTime' in rule) {
    return 'date';
  } else if ('Custom' in rule) {
    return 'custom';
  }
  throw new Error('Invalid rule in useValidationData');
};

// The anchor cell for a custom validation formula: the top-left cell of the
// validation's selection. Relative references in the formula are interpreted
// from this cell and shift for every other cell in the selection. The anchor
// is computed by the Rust core (formula_anchor) so the editor and the runtime
// can never disagree.
export const validationFormulaAnchor = (selection: A1Selection | undefined): { x: number; y: number; a1: string } => {
  if (selection) {
    try {
      const jsSelection = sheets.A1SelectionToJsSelection(selection);
      const anchor = jsSelection.getFormulaAnchor(sheets.jsA1Context);
      jsSelection.free();
      return { x: anchor.x, y: anchor.y, a1: getA1Notation(anchor.x, anchor.y) };
    } catch {
      // fall back to the cursor
    }
    const x = Number(selection.cursor.x);
    const y = Number(selection.cursor.y);
    return { x, y, a1: getA1Notation(x, y) };
  }
  return { x: 1, y: 1, a1: 'A1' };
};

export const validationUIType = (validation?: ValidationUndefined): ValidationUIType | undefined => {
  if (!validation || !('rule' in validation) || !validation.rule) return;
  const rule = validation.rule;
  if (rule === 'None') return;
  if ('List' in rule) {
    return 'dropdown';
  } else if ('Logical' in rule) {
    return 'checkbox';
  }
};
